import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { InputComponent } from '../input/input.component';
import {
  DropDownOption,
  GroupedDropDownOption,
  ValueChangeEvent,
} from '@kolytics/shared-components';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DropdownOptionsComponent } from '../dropdown-options/dropdown-options.component';
import { IconComponent } from '../../../../../../shared-assets/src/lib/components/icon/icon.component';
import { NgClass } from '@angular/common';

@UntilDestroy()
@Component({
    selector: 'klt-single-selection-group',
    templateUrl: './single-selection-group.component.html',
    styleUrls: ['./single-selection-group.component.scss'],
    standalone: true,
    imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    IconComponent,
    DropdownOptionsComponent
],
})
export class SingleSelectionGroupComponent
  extends InputComponent
  implements OnInit
{
  @ViewChild('input')
  inputElRef!: ElementRef;

  @ViewChild('wrapper') wrapper!: ElementRef;

  @Input()
  placeholder: string = '';
  @Input() allowEdit = false;
  @Input() allowAddNew = false;
  @Input() allowFilter = false;
  @Input() allowHttpFilter = false;

  expanded = false;
  localSelectedValue!: DropDownOption;

  options: DropDownOption[] = [];
  @Input() optionsGrouped!: GroupedDropDownOption[];
  filteredOptions: DropDownOption[] = [];

  @Output() selectionChange = new EventEmitter<DropDownOption>();
  @Output() afterInserting = new EventEmitter<DropDownOption>();
  @Output() afterEditing = new EventEmitter<DropDownOption>();
  @Output() afterHttpFiltering = new EventEmitter<string>();

  editing = false;
  adding = false;

  constructor(
    fb: FormBuilder,
    private _el: ElementRef,
  ) {
    super(fb);
  }

  private _searchable = false;

  get searchable(): boolean {
    return this._searchable;
  }

  @Input() set searchable(v: boolean) {
    this._searchable = v;
    if (v) {
      this.filteredOptions = [];
    }
  }

  private _items: DropDownOption[] = [];

  get items(): DropDownOption[] {
    return this._items;
  }

  @Input() set items(v: DropDownOption[]) {
    this._items = v;
    if (v) {
      this.options = v.map((item) => {
        const newItem = {
          ...item,
          name: item.label,
        };
        return Object.assign({}, newItem);
      });
      if (!this.searchable) {
        this.filteredOptions = this.options;
      }
    }
  }

  private _itemsGrouped!: GroupedDropDownOption[];

  get itemsGrouped(): GroupedDropDownOption[] {
    return this._itemsGrouped;
  }

  @Input() set itemsGrouped(v: GroupedDropDownOption[]) {
    this._itemsGrouped = v;
    if (v) {
      // v.data.forEach(item=>{
      //
      // })
      // this.optionsGrouped = v.options.map(itemGrouped => {
      //   const newItem = {
      //     ...itemGrouped,
      //     name: itemGrouped.label
      //   };
      //   return Object.assign({}, newItem);
      // });
    }
  }

  get selectedValue(): any {
    return this.localSelectedValue;
  }

  @Input() set selectedValue(value: any) {
    this.value = value;
  }

  get empty(): boolean {
    return this.filteredOptions.length <= 0;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.handleClickOutside();
    if (this.searchable) {
      this.valueChangedObservable
        .pipe(distinctUntilChanged())
        .subscribe((event: ValueChangeEvent) => {
          if (event.value) {
            this.formStates.focused = true;
            this.filteredOptions = this.options.filter(
              (e) =>
                e.label.toLowerCase().indexOf(event.value.toLowerCase()) > -1,
            );
            this.expanded = !this.empty;
          } else {
            this.formStates.error = false;
            this.filteredOptions = [...this.options];
          }
        });
    }
  }

  exist(value: string): DropDownOption | undefined {
    return this.options.find((e) => e.value === value);
  }

  toggle(event: Event): void {
    this.expanded = !this.expanded;
    this.inputElRef.nativeElement.focus();
  }

  private handleClickOutside() {
    fromEvent(document, 'click')
      .pipe(
        filter(({ target }) => {
          const origin = this.wrapper?.nativeElement;
          return origin.contains(target as HTMLElement) === false;
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (!this.editing && !this.adding) {
          if (this.expanded) {
            this.expanded = false;
          }
        }
      });
  }

  select(option: DropDownOption): void {
    this.localSelectedValue = option;
    this.selectedValue = option.label;
    this.filteredOptions = [...this.options];
    this.selectionChange.emit(option);
  }

  optionsChanged(options: DropDownOption[]): void {
    this.value = options.map((e) => e.label).join(', ');
    if (!this.value) {
      this.setLabelRaised(false);
    }
  }

  beforeSavingItem() {
    this.setLabelRaised(true);
  }

  afterSavingItem(item: DropDownOption) {
    if (this.options.findIndex((x) => x.value === item.value) === -1) {
      this.options.push(item);
      this.afterInserting.emit(item);
    } else {
      this.afterEditing.emit(item);
    }
  }

  onFilterOptionFocus() {
    this.setLabelRaised(true);
  }

  onFilterOptionBlur() {
    if (!this.localSelectedValue) {
      this.setLabelRaised(false);
    }
  }

  clear(): void {
    this.value = null;
    this.setLabelRaised(false);
  }

  save(options: DropDownOption[]): void {
    this.value = options.map((e) => e.label).join(', ');
  }

  onClosed(): void {
    this.expanded = false;
    if (!this.localSelectedValue) {
      this.setLabelRaised(false);
    }
  }

  setLabelRaised(state: boolean) {
    this.formStates.labelRaised = state;
  }
}
